import React from 'react';
import {
    GridColDef, GridToolbarContainer, GridToolbarFilterButton,
    GridFilterModel, GridFilterOperator, GridFilterItem, GridFilterInputValueProps,
    GridApiPro,
} from '@mui/x-data-grid-pro';
import { Box, Grid2 } from '@mui/material';
import { XGridTableWrap, FetchAutocomplete, useFetchData, TableColumns, UseFetchDataFullState } from '@tap/mui-ui';
import { IApiIssue, issueService, userService, IApiUserAccount, IApiDocumentSchema } from '@tap/data-providers';
import { IComponentCss } from '@tap/mui-ui';
import { IssueFilter } from './IssueFilter';
import { useSession } from '@tap/tap-ui';
import { TapPriorityList } from '@tap/tap-ui/src/models';
import { useDocuemtnSchema, getDocumentGridColumnList } from '@tap/doc-ui';
import { dateTimeHelper } from '@tap/utils';

/**
 * Таблица Заявок.
 */

type IssueTableProps = {
    columns: GridColDef<IApiIssue, any, any>[],
    rows?: IApiIssue[],
    isLoading?: boolean,
    issueDataState: UseFetchDataFullState<IApiIssue[]>,
    issueCodes?: string[],
    showFilter?: boolean,
    filterModel?: GridFilterModel,
    onFilterModelChange?: (filterModel: GridFilterModel) => void,
    xGridApiRef: React.MutableRefObject<GridApiPro>,
}

const css: IComponentCss = {
    tableWrap: {
        width: '100%',
        '& .hide-right-separator--header > .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
        },
        '& .MuiDataGrid-cell': {
            '& *': {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
            },
        },
    },
}


const CreatedBySelectValue = (props: GridFilterInputValueProps) => {

    const { item, applyValue } = props;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: 48,
                paddingBottom: '5px',
            }}
        >
            <FetchAutocomplete
                textFieldProps={{
                    variant: 'standard',
                    label: 'Значение',
                }}
                fetchRequest={({ searchText }) => {
                    return userService.getWorkerList({ searchText }).then((res) => res);
                }}
                value={item.value}
                onChange={(event: any, newValue: IApiUserAccount | null) => {
                    applyValue({ ...item, value: newValue });
                }}
                getOptionLabel={(option) => userService.getUserFIO(option)}
                noOptionsText={'Пользователь не найден'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
            />
        </Box>
    );
}


const createdByOperators: GridFilterOperator[] = [
    {
        label: 'равен',
        value: '=',
        InputComponent: CreatedBySelectValue,
        InputComponentProps: { type: 'object' },
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            if (
                !filterItem.field ||
                !filterItem.value ||
                !filterItem.operator
            ) {
                return null;
            }

            return (params): boolean => {
                return params.value === filterItem.value.id;
            };
        },
    },
];


const getColumns = (documentSchema: IApiDocumentSchema): TableColumns[] => {
    return [
        ...getDocumentGridColumnList(documentSchema.settings.gridColumns, documentSchema),
        {
            headerName: 'Тип',
            field: 'type',
            hide: true, // Нужно только для фильтра
            type: 'singleSelect',
            valueOptions: documentSchema.settings.typeList.map(i => ({ label: i.name, value: i.code })),
        },
        {
            headerName: 'Ключ',
            field: 'issueKey',
            hide: true, // Нужно только для фильтра 
        },
        {
            headerName: 'Статус',
            field: 'statusCode',
            hide: true, // Нужно только для фильтра
            type: 'singleSelect',
            valueOptions: documentSchema.settings.statusList.map(i => ({ label: i.name, value: i.code })),
        },
        {
            headerName: 'Приоритет',
            field: 'priority',
            hide: true, // Нужно только для фильтра
            type: 'singleSelect',
            valueOptions: TapPriorityList.map(i => ({ label: i.name, value: i.code })),
        },
        {
            headerName: 'Автор',
            field: 'createdBy',
            hide: true, // Нужно только для фильтра
            filterOperators: createdByOperators,
        },

        {
            headerName: 'Назначенные на меня',
            hide: true, // Нужно только для фильтра
            field: 'isMy',
            type: 'singleSelect',
            valueOptions: [
                { label: 'Да', value: true },
                { label: 'Нет', value: false },
            ],
        },

        {
            headerName: 'Неназначенные',
            field: 'isUnassignee',
            type: 'singleSelect',
            valueOptions: [
                { label: 'Да', value: true },
                { label: 'Нет', value: false },
            ],
        },
    ];
}

export const useIssueTable = (issueCodes: string[] | undefined) => {
    const { user } = useSession();
    const documentSchema = useDocuemtnSchema(issueCodes?.[0] || 'QC'); // TODO: !!!


    const { filterValue } = IssueFilter.useContext();

    const [issueDataState, issueData] = useFetchData(async () => {
        const filterStatuses = filterValue.statusCodes?.map(scode => documentSchema.settings.statusList.find(ts => ts.code === scode));

        let statusIds = filterStatuses?.map(s => s.id);
        if (!statusIds?.length) {
            statusIds = documentSchema.settings.statusList.map(s => s.id);
        }

        const list = (await issueService.getIssueList({
            issueCodes,
            fromUtc: dateTimeHelper.getStartOfDay(filterValue.dateFrom) ?? undefined,
            toUtc: dateTimeHelper.getEndOfDay(filterValue.dateTo) ?? undefined,
            manufactories: filterValue.manufactories,
            departmentIds: filterValue.departmentIds,
            statusCodes: filterValue.statusCodes,
            assigneeIds: filterValue.assigneeIds,
        })).rows;

        const autorIds = list.map(c => c.createdBy).filter(id => id > 0);
        const userPromise = autorIds.length === 0
            ? Promise.resolve([])
            : userService.getWorkerList({ workerIds: [...new Set(autorIds)] });

        const users = await userPromise;
        const userList = [...users, { id: -1, lastName: 'Система' }];

        return list.map(r => {
            const author = userList.find(u => u.id === r.createdBy);
            return {
                ...r,
                isUnassignee: !r.assigneeId,
                isMy: r.assigneeId === user?.userInfo.id,
                type: r.data?.type,
                author: author
                    ? {
                        ...author,
                        displayName: userService.getUserFIO(author),
                    }
                    : {
                        id: r.createdBy,
                        displayName: r.createdBy,
                    },
            }
        });
    }, [filterValue]);

    return [getColumns(documentSchema), issueData, issueDataState] as [
        GridColDef<IApiIssue, any, any>[],
        IApiIssue[] | undefined,
        UseFetchDataFullState<IApiIssue[]>,
    ];
}

export const IssueTable = ({
    columns,
    rows,
    issueDataState,
    showFilter = false,
    filterModel,
    onFilterModelChange,
    xGridApiRef,
}: IssueTableProps) => {
    return (
        <>
            <Grid2 sx={css.tableWrap}>
                <XGridTableWrap
                    rows={rows || []}
                    columns={columns}
                    dataIsLoading={issueDataState?.isLoading}
                    tableHeight={`calc(100vh - ${showFilter ? '158px' : '195px'})`}
                    tableProps={{
                        apiRef: xGridApiRef,
                        disableColumnFilter: !showFilter,
                        checkboxSelection: false,
                        filterModel: filterModel,
                        onFilterModelChange: onFilterModelChange,
                        slots: {
                            toolbar: showFilter
                                ? () => (
                                    <GridToolbarContainer sx={{ padding: '4px 5px', justifyContent: 'space-between' }}>
                                        <GridToolbarFilterButton sx={{ height: '28px' }} />
                                    </GridToolbarContainer>
                                ) : null,
                        },
                    }}
                />
            </Grid2>
        </>
    );
}

